// @flow
import Head from 'next/head';
import React, { Fragment, useContext } from 'react';
import { constants } from '@clubmed/utils';

import { Line, type LineProps } from 'Components/frameworks/line/Line';
import envConfig from 'Config';
import ReactAppContext from 'Context/ReactAppContext';
import { HOME } from 'Routes/constants';
import { STATIC_PAGE_TYPES } from 'Tracking/constants';
import { formatContacts } from 'Views/home/HomeView.helper';

import { fetchHomeConfig, fetchLegacyHomeConfig } from './HomeView.query';
import { HomeLegacyView } from './HomeLegacyView';

type Props = {
  config: {
    ...$Exact<CMSContent>,
    footer: CMSFooter,
  },
  page: {
    lines: $ReadOnlyArray<LineProps>,
  },
  displayOldHome: boolean,
};

const Home = ({ config, page, displayOldHome }: Props) => {
  const { locale, originalUrl: origin } = useContext(ReactAppContext);
  if (displayOldHome) {
    return <HomeLegacyView config={config} />;
  }
  const {
    seo,
    footer: {
      seo: { description },
    },
  } = config;

  const contactPoints = seo ? formatContacts(seo.contacts, locale) : [];
  const seoScript = (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          url: origin,
          name: constants.WEBSITE_NAME,
          description,
          ...(contactPoints.length === 0 ? null : { contactPoint: contactPoints }),
        }),
      }}
    />
  );

  return page ? (
    <Fragment>
      <Head>{seoScript}</Head>
      <Line props={{ lines: page.lines }} />
    </Fragment>
  ) : null;
};

Home.getInitialProps = async ({ appContext, location }: GetInitialPropsContext<>) => {
  const envDisplayOldHome = envConfig.get('featureFlipping.home.displayOldHome', appContext.locale);

  if (envDisplayOldHome) {
    const legacyHomeConfig = await fetchLegacyHomeConfig(appContext);
    return { ...legacyHomeConfig, displayOldHome: true };
  }
  const homeConfig = await fetchHomeConfig(appContext, location);

  return { ...homeConfig, displayOldHome: false };
};
Home.pageId = HOME;
Home.pageType = STATIC_PAGE_TYPES.SHOPPING_HOME;

export default Home;
